import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {combineLatest, Observable, of} from 'rxjs';
import {catchError, filter, map, tap} from 'rxjs/operators';
import {ConfigurationService, whenConfReady} from '../../../../entry-routes/configuration/configuration.service';
import {DomainItem} from '../../../models/domain-item';
import {Url} from '../../../models/url';
import {I18nService} from '../../../services/i18n/i18n.service';
import {CfgDocumentInfoService} from '../../cfg-document-info/service/cfg-document-info.service';
import {selectCurrentContextDatasCurrentDomain} from '../../current-context/current-context-selectors';
import {CurrentContextService} from '../../current-context/service/current-context.service';
import {DocumentDetailsService} from '../../details/service/document/document-details.service';
import {TaskDetailsService} from '../../details/service/task/task-details.service';
import {DocumentFilterService} from '../../filter/service/document-filter.service';
import {EntityFilterService} from '../../filter/service/entity-filter.service';
import {TaskFilterService} from '../../filter/service/task-filter.service';
import {MenuService} from '../../menu/service/menu.service';
import {DocumentSearchItemsService} from '../../search-item/service/document-search-item.service';
import {EntitySearchItemsService} from '../../search-item/service/entity-search-items.service';
import {TaskSearchItemsService} from '../../search-item/service/task-search-item.service';
import {SearchResultPaginationService} from '../../search-result-pagination/service/search-result-pagination.service';
import {DomainAction} from '../action/domain.action';
import {DomainState} from '../reducer/domain.reducer';
import {selectDomainsFeature} from '../selectors/domain.selectors';
import { ApplicationConfigVarNames } from '../../../../entry-routes/configuration/application-config-var-names';
import { EntityDetailsAction } from '../../details/action/entity/entity-details.action';
import {HttpErrorInterceptor} from '../../../../entry-routes/http-error-interceptor/http-error-interceptor';

@Injectable({
  providedIn: 'root'
})
export class DomainService {


  current: DomainItem;
  private domains$ = this.store.select(selectDomainsFeature).pipe(takeUntilDestroyed(), whenConfReady);
  private currentDomain$ = this.store.select(selectCurrentContextDatasCurrentDomain).pipe(takeUntilDestroyed(), whenConfReady);
  private domainsState: DomainState;
  private currentDomain: string;

  constructor(
    private documentFilterService: DocumentFilterService,
    private entityFilterService: EntityFilterService,
    private taskFilterService: TaskFilterService,
    private domainAction: DomainAction,
    private menuService: MenuService,
    private currentContextService: CurrentContextService,
    private httpClient: HttpClient,
    private documentDetailsService: DocumentDetailsService,
    private documentSearchItemsService: DocumentSearchItemsService,
    private entitySearchItemsService: EntitySearchItemsService,
    private taskSearchItemsService: TaskSearchItemsService,
    private taskDetailsService: TaskDetailsService,
    private cfgDocumentInfoService: CfgDocumentInfoService,
    private searchResultServicePagination: SearchResultPaginationService,
    private configService: ConfigurationService,
    private translateService: TranslateService,
    private store: Store,
    private i18nService: I18nService,
    private configurationActions: ConfigurationService,
    private entityDetailsAction: EntityDetailsAction
  ) {
    this.domains$.subscribe(domains => this.domainsState = domains);
    this.currentDomain$.subscribe(currentDomain => this.currentDomain = currentDomain);
  }

  public loadWorkspaceItems(): Observable<boolean> {
    console.debug(`CHARGEMENT des workspace items...`);
    this.domainAction.loadWorkspaceStarted();

    const domainsWkf = [];
    if (this.configurationActions.getConfigVariable(ApplicationConfigVarNames.WORKFLOW_ENABLED) === 'true'){
      const actifDomains$ = this.getActifDomainsWKF();
      actifDomains$.subscribe({
        next: (response: any[])=> {
           response.forEach(domainItem =>{
            domainsWkf.push(domainItem);
             console.debug(`CHARGEMENT des workspace items...domainsWkf : `, domainItem);
           })
        },
        error: (error) => {
          console.error('getActifDomains() erreur lors de la récupération de config des domaines', error);
        }
      })
    }

    const whenReady$ = this.configService.whenReady(() => this.httpClient.get(Url.getProtectedApiBaseUrl(this.configService) + Url.DOMAINS));

    const observable = combineLatest([whenReady$, this.i18nService.i18nLoaded$.pipe(filter(ready => ready))]);
    return observable
      .pipe(
        map(([response ]) => {
          return response;
        }),
        tap((response: any[]) => {
          let wsItems: DomainItem[] = [];
          response.forEach(element => {
            if(domainsWkf.indexOf(element.code) > -1){
              const item = this.createDomainItem(element, true);
              wsItems.push(item);
            }else{
              const item = this.createDomainItem(element, false);
              wsItems.push(item);
            }

          });
          let currentDomainCode: string = this.currentContextService.getCurrentDomain();
          if (!currentDomainCode && wsItems.length > 0) {
            currentDomainCode = wsItems[0].code;
            this.currentContextService.setCurrentDomain(currentDomainCode);
          }
          this.menuService.calcAccessToDomainTeamsByCurrentDomain(currentDomainCode);
          wsItems = wsItems.sort( (domain1,domain2) => domain1.label.toUpperCase().localeCompare(domain2.label.toUpperCase()));
          this.domainAction.loadWorkspaceSucceeded(wsItems);

        }),
        map(() => true),
        catchError(error => {
          console.error(error);
          this.domainAction.loadWorkspaceFailed(error);
          return of(false);
        }),
      );
  }

  public setCurrentWorkspace(...workspaceItems: DomainItem[]) {
    const domainCode = workspaceItems[0].code
    this.setCurrentDomain(domainCode);
  }

  public setCurrentDomainWithNavigation(domainCode: string, navigationUrlPart: string) {
    this.resetStore(navigationUrlPart); // si on navigue vers le détails d'un document on ne vide pas le store du détail du document  car il va être remplacé ...
    this.currentContextService.setCurrentDomain(domainCode);
    this.menuService.calcAccessToDomainTeamsByCurrentDomain(domainCode);
  }

  public setCurrentDomainIfChanged(domainCode: string) {
      if (this.currentContextService.getCurrentDomain() !== domainCode) {
        this.setCurrentDomain(domainCode)
      }
  }

  public setCurrentDomain(domainCode: string) {
    this.resetStore();
    this.currentContextService.setCurrentDomain(domainCode);
    this.menuService.calcAccessToDomainTeamsByCurrentDomain(domainCode);
  }

  public getDomains(): DomainItem[] {
    const domainState: DomainState = this.domainsState;
    if (!domainState) {
      return [];
    }
    return domainState.datas;
  }

  /**
   * @deprecated
   */
  getCurrentDomain(): DomainItem {
    const domainState: DomainState = this.domainsState;
    const currentDomainCode: string = this.currentDomain;
    domainState.datas.forEach(domainItem => {
      if (domainItem.code === currentDomainCode) {
        this.current = domainItem;
        return;
      }
    });
    return this.current;
  }

  private createDomainItem(responseItem: any, isWorkflow: boolean): DomainItem {
    const domainItem: DomainItem = new DomainItem();
    domainItem.code = responseItem.code;
    domainItem.label = this.translateService.instant('domain.' + responseItem.code + '.label');
    domainItem.text = this.translateService.instant('domain.' + responseItem.code + '.text');
    domainItem.entityType = responseItem.entityType.code;
    domainItem.region = responseItem.region;
    domainItem.entrypoint = responseItem.entrypoint;
    domainItem.isWorkflow = isWorkflow;
    return domainItem;
  }

  private resetStore(navigationUrlPart?:string): void {
    this.documentFilterService.removeAllFilters();
    this.entityFilterService.removeAllFilters();
    this.taskFilterService.removeAllFilters();
    this.cfgDocumentInfoService.removeAllCfgDocumentInfo();

    if(!navigationUrlPart)  {
        this.documentDetailsService.remove();
        this.taskDetailsService.remove();

        this.documentSearchItemsService.removeAllSearchItems();
        this.entitySearchItemsService.removeAllSearchItems();
        this.taskSearchItemsService.removeAllSearchItems();
        this.searchResultServicePagination.reinitSearchResultPagination();
    } else {
        if (navigationUrlPart !== Url.DOCUMENTS) {
            this.documentDetailsService.remove();
        }
        if (navigationUrlPart !== Url.TASKS) {
            this.taskDetailsService.remove();
        }
        if (navigationUrlPart !== Url.LINKS) {
            this.entityDetailsAction.removeAll();
        }
        if (navigationUrlPart !== Url.SEARCH) {
            this.documentSearchItemsService.removeAllSearchItems();
            this.entitySearchItemsService.removeAllSearchItems();
            this.taskSearchItemsService.removeAllSearchItems();
            this.searchResultServicePagination.reinitSearchResultPagination();
        }
    }


  }

  getActifDomainsWKF() : Observable<string[]> {
    const chunkHeaders = new HttpHeaders().set(HttpErrorInterceptor.BYPASS_HEADER, '')
    return this.httpClient.get<string[]>(Url.getFlowableProtectedApiBaseUrl(this.configService)+Url.WKF_NEXIA_API+'/domains',{headers: chunkHeaders})
      .pipe(
        catchError(err => {
          console.error(err);
          return of([]);
        })
      )
      ;
  }

  updateDomainsWithIsWorkflowInStore( domainsWithIsWorkflow: DomainItem[] ) {
    this.domainAction.updateDomainsWithIsWorkflowInStoreSuceed(domainsWithIsWorkflow);
  }
}
